import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

const CopilotClockBoard = ({
  score,
  homeAccent,
  awayAccent,
  onScoreChange,
  displayTimer,
  onTimerClick,
  onAddGoalClick,
  onRemoveGoalClick,
  onEditGoalClick,
  scale = 1,
}) => {
  // Scale the fixed-px clock geometry with the viewport; 1 at the 1080 baseline.
  const s = (px) => Math.round(px * scale);
  const handleScoreChange = (team, delta) => {
    if (onScoreChange) onScoreChange(team, delta);
  };

  const truncateName = (name) => {
    if (!name) return '';
    return name.length > 3 ? name.substring(0, 3).toUpperCase() : name.toUpperCase();
  };

  // A goal can only be removed when the team actually has goals on the board.
  const homeScore = Number(score?.home?.score) || 0;
  const awayScore = Number(score?.away?.score) || 0;

  // The team's score tile opens the edit window for its latest goal ("−" only
  // removes). Inert when there's nothing to edit.
  const tileEditProps = (team, teamScore) => (
    onEditGoalClick && teamScore > 0
      ? { role: 'button', onClick: () => onEditGoalClick(team) }
      : {}
  );
  const tileEditSx = (teamScore) => (
    onEditGoalClick && teamScore > 0 ? { cursor: 'pointer', '&:hover': { bgcolor: '#161616' } } : {}
  );

  // Game-status colour (status label only): yellow while awaiting kickoff or the
  // second half, white once live, blue when the game has finished.
  const periodColor = (() => {
    const s = String(score?.period || '').toUpperCase();
    if (/FULL[\s-]?TIME|FINISH/.test(s)) return '#4DA3FF';           // finished → blue
    if (/PRE/.test(s) || /HALF[\s-]?TIME/.test(s)) return '#FEEC67'; // awaiting kickoff / 2nd half → yellow
    return '#FFFFFF';                                                // live → white
  })();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 * scale, width: '100%', mb: 2 * scale, userSelect: 'none' }}>
       {/* Home Side */}
       <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 * scale }}>
         <IconButton
           disabled={homeScore <= 0}
           onClick={() => {
             if (onRemoveGoalClick) onRemoveGoalClick('home');
             else handleScoreChange('home', -1);
           }}
           sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.12)' } }}
         >
           <RemoveIcon sx={{ fontSize: s(44), fontWeight: 'bold' }} />
         </IconButton>
         
         <Box {...tileEditProps('home', homeScore)} sx={{
           width: s(80), height: s(90),
           bgcolor: '#0a0a0a',
           display: 'flex', flexDirection: 'column',
           alignItems: 'center', justifyContent: 'center',
           borderBottom: `4px solid ${homeAccent || '#d10d0d'}`,
           borderRadius: 1,
           ...tileEditSx(homeScore),
         }}>
            <Typography sx={{ color: '#fff', fontSize: s(52), fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{score?.home?.score || 0}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: s(11), fontWeight: 600, letterSpacing: '0.14em', mt: 0.5 * scale }}>{truncateName(score?.home?.team) || 'HOM'}</Typography>
         </Box>

         <IconButton 
           onClick={() => {
             if (onAddGoalClick) onAddGoalClick('home');
             else handleScoreChange('home', 1);
           }}
           sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff' } }}
         >
           <AddIcon sx={{ fontSize: s(44), fontWeight: 'bold' }} />
         </IconButton>
       </Box>

       {/* Clock Center */}
       <Box onClick={onTimerClick} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', minWidth: s(160) }}>
          <Typography sx={{ color: periodColor, fontSize: s(12), fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase' }}>
            {score?.period || 'HALF TIME'}
          </Typography>
          <Typography sx={{ color: '#fff', fontSize: s(64), fontWeight: 600, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {displayTimer || '00:00'}
          </Typography>
       </Box>

       {/* Away Side */}
       <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 * scale }}>
         <IconButton
           disabled={awayScore <= 0}
           onClick={() => {
             if (onRemoveGoalClick) onRemoveGoalClick('away');
             else handleScoreChange('away', -1);
           }}
           sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.12)' } }}
         >
           <RemoveIcon sx={{ fontSize: s(44), fontWeight: 'bold' }} />
         </IconButton>
         
         <Box {...tileEditProps('away', awayScore)} sx={{
           width: s(80), height: s(90),
           bgcolor: '#0a0a0a',
           display: 'flex', flexDirection: 'column',
           alignItems: 'center', justifyContent: 'center',
           borderBottom: `4px solid ${awayAccent || '#0044ff'}`,
           borderRadius: 1,
           ...tileEditSx(awayScore),
         }}>
            <Typography sx={{ color: '#fff', fontSize: s(52), fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{score?.away?.score || 0}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: s(11), fontWeight: 600, letterSpacing: '0.14em', mt: 0.5 * scale }}>{truncateName(score?.away?.team) || 'AWA'}</Typography>
         </Box>

         <IconButton 
           onClick={() => {
             if (onAddGoalClick) onAddGoalClick('away');
             else handleScoreChange('away', 1);
           }}
           sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#fff' } }}
         >
           <AddIcon sx={{ fontSize: s(44), fontWeight: 'bold' }} />
         </IconButton>
       </Box>
    </Box>
  );
};

CopilotClockBoard.propTypes = {
  score: PropTypes.object,
  homeAccent: PropTypes.string,
  awayAccent: PropTypes.string,
  onScoreChange: PropTypes.func,
  displayTimer: PropTypes.string,
  onTimerClick: PropTypes.func,
  onAddGoalClick: PropTypes.func,
  onRemoveGoalClick: PropTypes.func,
  onEditGoalClick: PropTypes.func,
  scale: PropTypes.number,
};

export default CopilotClockBoard;
